/**
 *
 * "Main module to generate 3D deck canvas from the components"
 *
 * Note: The 3D canvas. This uses react-three-fiber,
 * so take care with adding unnecessary data in.
 * The more complicated, the more it has to redraw the 3D scene.
 *
 * @file   DeckCanvas.js
 * @author Lateral
 * @since  2023
 */
import { Canvas } from '@react-three/fiber'
import { Paper, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React, { useState, Suspense, memo } from 'react'
import deg2Rad from './common/angles'
import DeckModel from './components/DeckModel/DeckModel'
import DeckLabels from './components/DeckLabels/DeckLabels'
import Inserter from './components/Inserter/Inserter'
import DeckGridCoordinates from './components/DeckGridCoordinates/DeckGridCoordinates'
import withPropsChecker from 'components/withWhatPropChanged'

const rotation = [deg2Rad(-40), deg2Rad(32), deg2Rad(24)]

export const DeckCanvas = withPropsChecker(
  memo(function DeckCanvas_(props) {
    return (
      <Suspense fallback={<Typography sx={{ p: '16px' }}>Loading Deck Revision...</Typography>}>
        <InnerDeckCanvas {...props}></InnerDeckCanvas>
      </Suspense>
    )
  }),
  'DeckCanvas'
)

function InnerDeckCanvas({ data, maintenance, heatMapData, selected, materialSelected, saveDeckRevision, mode }) {
  /**
   * Generates 3D deck canvas
   *
   * @function
   * @param {object} data - Deck rivision object from database
   * @param {object} maintenance - Deck Revision Histories
   * @param {object} heatMapData - Heat map data of all anels and sideliner
   * @param {} onPanelClick - Action to click on a panel/sideliners. Same action applicable for panels and sideliners
   * @param {} onMaintenanceTypeClick - Action to click on Maintenance type in Maintenance mode
   * @param {object[]} selected - Array of selected panel objects
   * @param {number} materialSelected - Selected sideliner material number
   * @param {object} mouseCanvasPosition - x and y coordinates of pointer on canvas
   * @param {} onMaintenanceTypeClick - Action on changing panel/sideliner selection
   * @param {} onWearSubmit - Action on submiting wear in Maintenance mode
   * @param {} saveDeckRevision - Function to save deckrivision hystory
   * @param {number} awaitingActionId - id of await action for asynch operation
   * @param {number} cancelAwaitingAction - Id of await action when click cancel in maintenance mode
   * @param {string} mode - Current mode of deck layout
   * @param {} onUndo - Aaction on clicking undo button in maintenance mode
   * @param {} onRedo - Aaction on clicking redo button in maintenance mode
   * @param {} onLoadTrial - Aaction on clicking Load Trial button
   * @param {boolean} isHistoryEmpty - True if the history of deck revision is empty else false. Default: True
   * @param {boolean} isHistoryAtEnd - True if the current history is the last of revison histories
   *
   * @returns {object} - React element of 3D deck canvas
   */

  const [isInserting, setIsInserting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  //note: theming cannot be passed down the canvas with useTheme, so we have to pass it as a prop
  const theme = useTheme()

  function onCancel() {
    setIsInserting(false)
    setIsDeleting(false)
  }

  if (Object.keys(data).length === 0) {
    return null
  }

  return (
    <Paper
      elevation={8}
      sx={{
        height: '100%',
        gridTemplateRows: 'auto 1fr',
        backgroundColor: 'transparent',
        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05));'
      }}>
      {/* The canvas. 3D from here on. */}
      <Canvas dpr={[1, 2]} flat orthographic camera={{ zoom: 16, position: [0, 0, 100] }} frameloop="demand">
        <ambientLight intensity={0.5} />
        <directionalLight intensity={1.2} />
        {!isInserting && !isDeleting ? (
          <DeckLabels deckHeight={data.Size?.Rows} deckWidth={data.Size?.Columns} rotation={rotation} deckMode={mode} />
        ) : null}{' '}
        <DeckModel
          data={data}
          maintenance={maintenance}
          heatMapData={heatMapData}
          rotation={rotation}
          selected={selected}
          materialSelected={materialSelected}
          theme={theme}
          deckMode={mode}
        />
        {(isInserting || isDeleting) && saveDeckRevision ? (
          <Inserter
            data={data}
            theme={theme}
            rotation={rotation}
            onComplete={onCancel}
            isDeleting={isDeleting}
            isInserting={isInserting}
            saveDeckRevision={saveDeckRevision}
          />
        ) : null}
        <DeckGridCoordinates data={data} rotation={rotation} />
      </Canvas>
    </Paper>
  )
}

export default DeckCanvas
